/**
 * Utilitaires pour les appels API authentifiés
 * Simplifie l'utilisation de MSAL avec Axios
 */

import Axios, { AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { getAuthService } from '@/msal/index'

type ApiMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
type ApiTimeoutProfile = 'fast' | 'default' | 'compute' | 'download'
type ApiRequestParams = Record<string, unknown>

const API_TIMEOUTS: Record<ApiTimeoutProfile, number> = {
  fast: 10 * 1000,
  default: 30 * 1000,
  compute: 180 * 1000,
  download: 120 * 1000,
}

const RETRYABLE_METHODS = new Set<ApiMethod>(['GET'])
const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504])
const inFlightRequests = new Map<string, Promise<AxiosResponse<unknown>>>()

function isAxiosErrorLike(error: unknown): error is AxiosError {
  return !!error && typeof error === 'object' && 'isAxiosError' in error
}

export class ApiError extends Error {
  status?: number
  code?: string
  type: 'auth' | 'network' | 'timeout' | 'validation' | 'unknown'
  userMessage: string
  cause?: unknown

  constructor(
    message: string,
    options: {
      status?: number
      code?: string
      type?: 'auth' | 'network' | 'timeout' | 'validation' | 'unknown'
      userMessage?: string
      cause?: unknown
    } = {}
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = options.status
    this.code = options.code
    this.type = options.type || 'unknown'
    this.userMessage = options.userMessage || message
    this.cause = options.cause
  }
}

/**
 * Normalise une URL en supprimant les doubles slashes dans le path
 * @param url - URL à normaliser
 * @returns URL normalisée
 * 
 * @example
 * normalizeUrl('https://localhost:51026//api/test') 
 * // => 'https://localhost:51026/api/test'
 */
function normalizeUrl(url: string): string {
  // Sépare le protocole du reste de l'URL
  const protocolMatch = url.match(/^(https?:\/\/)(.*)/)
  if (protocolMatch) {
    const protocol = protocolMatch[1]
    const rest = protocolMatch[2]
    // Remplace tous les doubles slashes (ou plus) par un seul slash dans le reste de l'URL
    return protocol + rest.replace(/\/+/g, '/')
  }
  // Si pas de protocole, remplace simplement les doubles slashes
  return url.replace(/\/+/g, '/')
}

/**
 * Interface pour les options de requête API
 */
interface ApiRequestOptions<T = unknown> {
  url: string
  method?: ApiMethod
  data?: unknown
  params?: ApiRequestParams
  headers?: Record<string, string>
  responseType?: 'json' | 'blob' | 'arraybuffer' | 'text'
  timeoutMs?: number
  timeoutProfile?: ApiTimeoutProfile
  retries?: number
  retryDelayMs?: number
  dedupe?: boolean
  dedupeKey?: string
  signal?: AbortSignal
  validateResponse?: (data: T) => boolean
}

type ApiMutationConfig<T = unknown> = Omit<ApiRequestOptions<T>, 'url' | 'method'>

function isApiMutationConfig(value: unknown): value is ApiMutationConfig {
  if (!value || typeof value !== 'object') {
    return false
  }

  return ['data', 'params', 'headers', 'responseType', 'timeoutMs', 'timeoutProfile', 'retries', 'retryDelayMs', 'dedupe', 'dedupeKey', 'signal', 'validateResponse']
    .some(key => key in value)
}

function resolveTimeoutMs<T>(options: ApiRequestOptions<T>): number {
  if (options.timeoutMs) {
    return options.timeoutMs
  }

  if (options.timeoutProfile) {
    return API_TIMEOUTS[options.timeoutProfile]
  }

  if (options.responseType === 'blob' || options.responseType === 'arraybuffer') {
    return API_TIMEOUTS.download
  }

  return API_TIMEOUTS.default
}

function stableStringify(value: unknown): string {
  if (value === null || value === undefined) {
    return String(value)
  }

  if (typeof value !== 'object') {
    return JSON.stringify(value)
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`
  }

  const entries = Object.entries(value as Record<string, unknown>)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, nestedValue]) => `${JSON.stringify(key)}:${stableStringify(nestedValue)}`)

  return `{${entries.join(',')}}`
}

function buildRequestKey<T>(options: ApiRequestOptions<T>, normalizedUrl: string): string {
  return [
    options.method || 'GET',
    normalizedUrl,
    stableStringify(options.params),
    stableStringify(options.data),
    options.responseType || 'json',
  ].join('|')
}

function sleep(delayMs: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, delayMs))
}

function isTimeoutError(error: AxiosError): boolean {
  return error.code === 'ECONNABORTED' || error.message.toLowerCase().includes('timeout')
}

function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error
  }

  if (isAxiosErrorLike(error)) {
    const axiosError = error as AxiosError
    const status = axiosError.response?.status

    if (isTimeoutError(axiosError)) {
      return new ApiError('Request timeout', {
        status,
        code: axiosError.code,
        type: 'timeout',
        userMessage: 'Le service a mis trop de temps a repondre. Reessayez dans quelques instants.',
        cause: error,
      })
    }

    if (status === 401 || status === 403) {
      return new ApiError('Authentication required', {
        status,
        code: axiosError.code,
        type: 'auth',
        userMessage: 'Votre session a expire ou vos droits ne permettent plus cet acces.',
        cause: error,
      })
    }

    if (!axiosError.response) {
      return new ApiError('Network error', {
        code: axiosError.code,
        type: 'network',
        userMessage: 'Impossible de contacter le service distant. Verifiez votre connexion puis reessayez.',
        cause: error,
      })
    }

    if (status && RETRYABLE_STATUS_CODES.has(status)) {
      return new ApiError('Service unavailable', {
        status,
        code: axiosError.code,
        type: 'network',
        userMessage: 'Le service est temporairement indisponible. Reessayez dans quelques instants.',
        cause: error,
      })
    }

    return new ApiError(axiosError.message || 'Request failed', {
      status,
      code: axiosError.code,
      type: 'unknown',
      userMessage: axiosError.message || 'Une erreur inattendue est survenue lors de l\'appel API.',
      cause: error,
    })
  }

  if (error instanceof Error) {
    return new ApiError(error.message, {
      type: 'unknown',
      userMessage: error.message,
      cause: error,
    })
  }

  return new ApiError('Unknown error', {
    type: 'unknown',
    userMessage: 'Une erreur inattendue est survenue lors de l\'appel API.',
    cause: error,
  })
}

function shouldRetryRequest(error: ApiError, method: ApiMethod, attempt: number, retries: number): boolean {
  if (attempt >= retries) {
    return false
  }

  if (!RETRYABLE_METHODS.has(method)) {
    return false
  }

  return error.type === 'timeout' || error.type === 'network'
}

export function getUserFacingErrorMessage(error: unknown, fallback = 'Une erreur inattendue est survenue.'): string {
  return error instanceof ApiError ? error.userMessage : fallback
}

export function isAuthenticationError(error: unknown): boolean {
  return error instanceof ApiError && error.type === 'auth'
}

/**
 * Effectue un appel API authentifié avec MSAL
 * @param options - Options de la requête
 * @returns Promise de la réponse Axios
 */
export async function authenticatedRequest<T = any>(
  options: ApiRequestOptions<T>
): Promise<AxiosResponse<T>> {
  const authService = getAuthService()
  const tokenConfig = await authService.getAxiosTokenConfig()
  
  if (!tokenConfig) {
    throw new ApiError('Unable to get authentication token', {
      type: 'auth',
      userMessage: 'Votre session a expire. Veuillez vous reconnecter.',
    })
  }

  const normalizedUrl = normalizeUrl(options.url)
  const method = options.method || 'GET'
  const retries = options.retries ?? (RETRYABLE_METHODS.has(method) ? 2 : 0)
  const retryDelayMs = options.retryDelayMs ?? 750
  const dedupe = options.dedupe ?? method === 'GET'
  const requestKey = options.dedupeKey || buildRequestKey({ ...options, method }, normalizedUrl)
  
  const executeRequest = async (): Promise<AxiosResponse<T>> => {
    for (let attempt = 0; ; attempt++) {
      const config: AxiosRequestConfig = {
        url: normalizedUrl,
        method,
        data: options.data,
        params: options.params,
        headers: {
          ...tokenConfig.headers,
          ...options.headers,
        },
        responseType: options.responseType || 'json',
        timeout: resolveTimeoutMs(options),
        signal: options.signal,
      }

      try {
        const response = await Axios(config)

        if (options.validateResponse && !options.validateResponse(response.data as T)) {
          throw new ApiError('Invalid API response', {
            type: 'validation',
            userMessage: 'Le service a retourne une reponse inattendue. Rechargez la page puis reessayez.',
          })
        }

        return response as AxiosResponse<T>
      } catch (error) {
        const apiError = toApiError(error)

        if (!shouldRetryRequest(apiError, method, attempt, retries)) {
          throw apiError
        }

        await sleep(retryDelayMs * (attempt + 1))
      }
    }
  }

  if (!dedupe) {
    return executeRequest()
  }

  const existingRequest = inFlightRequests.get(requestKey)
  if (existingRequest) {
    return existingRequest as Promise<AxiosResponse<T>>
  }

  const pendingRequest = executeRequest().finally(() => {
    inFlightRequests.delete(requestKey)
  })

  inFlightRequests.set(requestKey, pendingRequest as Promise<AxiosResponse<unknown>>)
  return pendingRequest
}

/**
 * GET authentifié
 */
export async function get<T = any>(
  url: string,
  config?: Omit<ApiRequestOptions<T>, 'url' | 'method'>
): Promise<AxiosResponse<T>> {
  return authenticatedRequest<T>({ 
    url, 
    method: 'GET', 
    ...config 
  })
}

/**
 * POST authentifié
 */
export async function post<T = any>(
  url: string,
  dataOrConfig?: unknown | ApiMutationConfig<T>
): Promise<AxiosResponse<T>> {
  // Si c'est un objet avec data, params, headers, etc., le passer tel quel
  // Sinon, le considérer comme data
  const config = isApiMutationConfig(dataOrConfig)
    ? dataOrConfig
    : { data: dataOrConfig }
  
  return authenticatedRequest<T>({ 
    url, 
    method: 'POST', 
    ...config 
  })
}

/**
 * PUT authentifié
 */
export async function put<T = any>(
  url: string,
  dataOrConfig?: unknown | ApiMutationConfig<T>
): Promise<AxiosResponse<T>> {
  // Si c'est un objet avec data, params, headers, etc., le passer tel quel
  // Sinon, le considérer comme data
  const config = isApiMutationConfig(dataOrConfig)
    ? dataOrConfig
    : { data: dataOrConfig }
  
  return authenticatedRequest<T>({ 
    url, 
    method: 'PUT', 
    ...config 
  })
}

/**
 * DELETE authentifié
 */
export async function del<T = any>(
  url: string,
  config?: Omit<ApiRequestOptions<T>, 'url' | 'method'>
): Promise<AxiosResponse<T>> {
  return authenticatedRequest<T>({ url, method: 'DELETE', ...config })
}

/**
 * Télécharge un fichier blob
 */
export async function downloadBlob(
  url: string,
  filename: string
): Promise<void> {
  const response = await authenticatedRequest<Blob>({
    url,
    method: 'GET',
    responseType: 'blob',
  })

  // Crée un lien de téléchargement temporaire
  const blob = new Blob([response.data])
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(blob)
  link.download = filename
  link.click()
  window.URL.revokeObjectURL(link.href)
}

/**
 * Hook pour utiliser les utilitaires API dans les composants Vue 3
 * @returns Utilitaires API
 */
export function useApi() {
  return {
    get,
    post,
    put,
    delete: del,
    downloadBlob,
    authenticatedRequest,
  }
}

// Alias de compatibilité pour le code existant
export const authenticatedGet = get
export const authenticatedPost = post
export const authenticatedPut = put
export const authenticatedDelete = del

export default {
  ApiError,
  authenticatedRequest,
  get,
  post,
  put,
  delete: del,
  downloadBlob,
  getUserFacingErrorMessage,
  isAuthenticationError,
  useApi,
  // Alias de compatibilité
  authenticatedGet,
  authenticatedPost,
  authenticatedPut,
  authenticatedDelete,
}
